import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { TenantRegistrationModal } from '../components/TenantRegistrationModal';

/**
 * 商家註冊 Landing Page（使用 Google 登入）
 * 支持已登入用戶註冊新商家
 */
export function TenantRegistration() {
  const [isLoading, setIsLoading] = useState(false);
  const [showRegistrationModal, setShowRegistrationModal] = useState(false);
  const [token, setToken] = useState<string | null>(null);

  // 處理 OAuth 回調（從 URL 參數取得 token）
  useEffect(() => {
    // 立即記錄當前 URL（用於除錯）
    console.log('🔍 TenantRegistration useEffect 執行:', {
      pathname: window.location.pathname,
      search: window.location.search,
      href: window.location.href,
    });

    // 從當前 URL 的查詢參數取得 token（支援完整 URL 或相對路徑）
    const urlParams = new URLSearchParams(window.location.search);
    let token = urlParams.get('token');
    let tenantId = urlParams.get('tenant_id');
    let isNew = urlParams.get('is_new') === '1';
    const action = urlParams.get('action'); // 檢查是否有 action=create 參數
    const needsRegistration =
      urlParams.get('needs_tenant_registration') === '1';

    // 如果從 URL 參數沒有取得，嘗試從完整 URL 解析
    if (!token || !tenantId) {
      const fullUrl = new URL(window.location.href);
      token = token || fullUrl.searchParams.get('token');
      tenantId = tenantId || fullUrl.searchParams.get('tenant_id');
      isNew = isNew || fullUrl.searchParams.get('is_new') === '1';
    }

    // 檢查是否為註冊新商家流程（action=create）
    const isCreateNewTenant = action === 'create' || needsRegistration;

    // 如果已經有 token 且 URL 沒有參數，檢查是否要註冊新商家
    const existingToken = localStorage.getItem('tenant_token');
    if (existingToken && !token && !tenantId) {
      // 如果 URL 有 action=create 參數，顯示註冊表單
      if (isCreateNewTenant) {
        console.log('🔍 已登入用戶要註冊新商家，顯示註冊表單');
        setToken(existingToken);
        setShowRegistrationModal(true);
        // 清除 URL 參數
        window.history.replaceState({}, '', '/tenant-register?action=create');
        return;
      }
      console.log('ℹ️ 已有 token，跳過處理');
      return;
    }

    // 除錯：記錄 URL 參數
    console.log('🔍 OAuth 回調檢查:', {
      hasToken: !!token,
      hasTenantId: !!tenantId,
      isNew,
      isCreateNewTenant,
      action,
      needsRegistration,
      url: window.location.href,
      searchParams: window.location.search,
      tokenLength: token?.length || 0,
      tenantIdValue: tenantId,
    });

    // 如果是註冊新商家流程，且已有 token（來自 OAuth 回調），顯示註冊表單
    // 重要：先將「本次 OAuth 的 token」寫入 localStorage，避免 replaceState 後 re-render 讀到舊 token，
    // 導致建立 tenant 時用錯 JWT（舊 userId）而後端建立「Test User」或找不到用戶
    if (token && isCreateNewTenant) {
      console.log('🔍 註冊新商家流程，顯示註冊表單（寫入本次 OAuth token）');
      localStorage.setItem('tenant_token', token);
      localStorage.removeItem('tenant_id'); // 清除舊 tenant_id，避免殘留上一筆商家
      window.dispatchEvent(new Event('localStorageChange'));
      setToken(token);
      setShowRegistrationModal(true);
      // 清除 URL 參數中的 token 等敏感資訊，但保留 action=create
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.delete('token');
      newUrl.searchParams.delete('tenant_id');
      newUrl.searchParams.delete('is_new');
      newUrl.searchParams.delete('needs_tenant_registration');
      newUrl.searchParams.delete('tenants');
      if (!newUrl.searchParams.has('action')) {
        newUrl.searchParams.set('action', 'create');
      }
      window.history.replaceState({}, '', newUrl.toString());
      return;
    }

    if (token && tenantId) {
      // 使用 async 函數處理非同步操作
      const handleTokenStorage = async () => {
        try {
          // 儲存 token 到 localStorage
          localStorage.setItem('tenant_token', token);
          localStorage.setItem('tenant_id', tenantId);

          // 觸發自定義事件，通知其他組件 localStorage 已變化
          window.dispatchEvent(new Event('localStorageChange'));

          // 驗證是否成功儲存
          const savedToken = localStorage.getItem('tenant_token');
          const savedTenantId = localStorage.getItem('tenant_id');

          console.log('✅ Token 儲存結果:', {
            saved: !!savedToken,
            tokenLength: savedToken?.length || 0,
            tenantId: savedTenantId,
          });

          if (!savedToken || savedToken !== token) {
            console.error('❌ Token 儲存失敗！');
            toast.error('無法儲存登入資訊，請重試');
            return;
          }

          // 等待一下確保 localStorage 已寫入
          await new Promise(resolve => setTimeout(resolve, 100));

          // 再次驗證 token 是否成功儲存
          const finalToken = localStorage.getItem('tenant_token');
          if (!finalToken || finalToken !== token) {
            console.error('❌ Token 最終驗證失敗！');
            toast.error('無法儲存登入資訊，請重試');
            return;
          }

          // 自動註冊裝置（Member 登入時）
          const { autoRegisterDevice } = await import(
            '../utils/deviceToken.js'
          );
          await autoRegisterDevice(tenantId!, token!);

          // 先清除 URL 參數（避免重複執行）
          window.history.replaceState({}, '', '/tenant-register');

          if (isNew) {
            toast.success('商家註冊成功！', {
              duration: 5000,
            });
            // 導向設定頁面（延遲確保 token 已儲存）
            setTimeout(() => {
              window.location.href = '/tenant-settings';
            }, 1500);
          } else {
            toast.success('登入成功！', {
              duration: 3000,
            });
            // 導向設定頁面（延遲確保 token 已儲存）
            setTimeout(() => {
              window.location.href = '/tenant-settings';
            }, 1500);
          }
        } catch (error) {
          console.error('❌ 儲存 token 時發生錯誤:', error);
          toast.error('儲存登入資訊失敗，請重試');
        }
      };

      // 執行 async 函數
      handleTokenStorage();
    } else if (
      window.location.search.includes('token=') ||
      window.location.search.includes('tenant_id=')
    ) {
      // 如果有部分參數但缺少關鍵資訊
      console.warn('⚠️ URL 參數不完整:', {
        token: token ? '有' : '無',
        tenantId: tenantId ? '有' : '無',
        search: window.location.search,
        fullUrl: window.location.href,
      });

      // 嘗試從完整 URL 再次解析
      try {
        const fullUrlObj = new URL(window.location.href);
        const retryToken = fullUrlObj.searchParams.get('token');
        const retryTenantId = fullUrlObj.searchParams.get('tenant_id');

        if (retryToken && retryTenantId) {
          console.log('🔄 從完整 URL 重新解析成功，開始處理 token...');
          // 遞迴調用處理邏輯
          localStorage.setItem('tenant_token', retryToken);
          localStorage.setItem('tenant_id', retryTenantId);
          console.log('✅ 已從完整 URL 儲存 token');
          // 清除 URL 參數並重新載入
          window.history.replaceState({}, '', '/tenant-register');
          setTimeout(() => {
            window.location.href = '/tenant-settings';
          }, 500);
          return;
        }
      } catch (error) {
        console.error('❌ 解析完整 URL 失敗:', error);
      }

      toast.error('登入資訊不完整，請重新登入');
    }
  }, []); // 空依賴陣列，只在組件掛載時執行一次

  // 額外監聽 URL 變化（用於處理 OAuth 回調，當組件已經掛載時）
  useEffect(() => {
    let lastSearch = window.location.search;

    const checkUrlParams = () => {
      const currentSearch = window.location.search;
      if (currentSearch !== lastSearch) {
        lastSearch = currentSearch;
        const urlParams = new URLSearchParams(currentSearch);
        const token = urlParams.get('token');
        const tenantId = urlParams.get('tenant_id');

        // 如果 URL 有參數但 localStorage 沒有 token，重新處理
        if (token && tenantId && !localStorage.getItem('tenant_token')) {
          console.log('🔍 檢測到 URL 參數變化，重新處理 token...');
          localStorage.setItem('tenant_token', token);
          localStorage.setItem('tenant_id', tenantId);
          console.log('✅ 已從 URL 參數儲存 token');
          // 觸發頁面重新載入以確保狀態更新
          window.location.reload();
        }
      }
    };

    // 定期檢查 URL 參數變化（每 200ms）
    const interval = setInterval(checkUrlParams, 200);

    return () => clearInterval(interval);
  }, []);

  /**
   * 開始 Google 登入流程
   */
  const handleGoogleLogin = () => {
    setIsLoading(true);
    const apiBaseUrl =
      import.meta.env.VITE_API_BASE_URL || 'http://localhost:8787';

    // 檢查是否已登入
    const existingToken = localStorage.getItem('tenant_token');
    if (existingToken) {
      // 已登入，直接顯示註冊表單
      console.log('🔍 已登入，直接顯示註冊表單');
      setToken(existingToken);
      setShowRegistrationModal(true);
      setIsLoading(false);
      return;
    }

    // 未登入，進行 OAuth 登入
    const redirectUri = `${window.location.origin}/tenant-register?action=create`;
    window.location.href = `${apiBaseUrl}/api/auth/google?redirect_uri=${encodeURIComponent(redirectUri)}`;
  };

  /**
   * 處理註冊成功
   */
  const handleRegistrationSuccess = async (newTenantId: string) => {
    console.log('✅ 商家註冊成功:', newTenantId);
    toast.success('商家註冊成功！');

    // 確保 token 和 tenant_id 都已保存到 localStorage
    // 優先使用 state 中的 token，否則從 localStorage 取得
    const currentToken = token || localStorage.getItem('tenant_token');

    if (!currentToken) {
      console.error('❌ 無法取得 token，無法繼續');
      toast.error('登入資訊遺失，請重新登入');
      setTimeout(() => {
        window.location.href = '/';
      }, 2000);
      return;
    }

    // 保存 token 和 tenant_id
    localStorage.setItem('tenant_token', currentToken);
    localStorage.setItem('tenant_id', newTenantId);

    // 觸發自定義事件，通知其他組件 localStorage 已變化
    window.dispatchEvent(new Event('localStorageChange'));

    // 驗證是否成功保存
    const savedToken = localStorage.getItem('tenant_token');
    const savedTenantId = localStorage.getItem('tenant_id');

    if (
      !savedToken ||
      savedToken !== currentToken ||
      savedTenantId !== newTenantId
    ) {
      console.error('❌ Token 或 tenant_id 保存失敗');
      toast.error('無法保存登入資訊，請重試');
      return;
    }

    console.log('✅ Token 和 tenant_id 已成功保存:', {
      tokenLength: savedToken.length,
      tenantId: savedTenantId,
    });

    // 在遠端環境，Service Worker 可能會快取頁面，需要確保 localStorage 已寫入
    // 使用更長的延遲確保在遠端環境也能正常工作
    await new Promise(resolve => setTimeout(resolve, 300));

    // 再次驗證（確保在延遲期間沒有被清除）
    const finalToken = localStorage.getItem('tenant_token');
    const finalTenantId = localStorage.getItem('tenant_id');

    if (
      !finalToken ||
      finalToken !== currentToken ||
      finalTenantId !== newTenantId
    ) {
      console.error('❌ 延遲後驗證失敗，token 或 tenant_id 遺失');
      toast.error('無法保存登入資訊，請重試');
      return;
    }

    // 重定向到 staff-setup 頁面，讓用戶設定名稱和 PIN code
    // 使用 window.location.replace 避免用戶可以返回
    // 添加時間戳參數強制 Service Worker 不使用快取（僅在遠端環境）
    const isRemote =
      window.location.hostname !== 'localhost' &&
      window.location.hostname !== '127.0.0.1';
    const redirectUrl = isRemote
      ? `/staff-setup?_t=${Date.now()}&_tenant=${newTenantId}`
      : '/staff-setup';

    console.log('🔄 準備重定向到 staff-setup:', redirectUrl);
    window.location.replace(redirectUrl);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      {/* 返回連結 */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">
        <a
          href="/"
          className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 transition-colors"
        >
          <svg
            className="w-4 h-4 mr-2"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
          返回首頁
        </a>
      </div>

      {/* 主要內容 */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* 標題區塊 */}
        <div className="text-center mb-12">
          <h1 className="text-5xl font-bold text-gray-900 mb-4">商家註冊</h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            使用 Google 帳號快速註冊，立即開始使用 POS 系統與預訂功能
          </p>
        </div>

        {/* 登入卡片 */}
        <div
          className="bg-white rounded-2xl shadow-xl p-8 md:p-12 max-w-2xl mx-auto"
          style={{
            backgroundColor: 'rgba(255, 255, 255, 1)',
            backgroundImage: 'none',
          }}
        >
          <div className="text-center">
            {/* Google 登入按鈕 */}
            <button
              onClick={handleGoogleLogin}
              disabled={isLoading}
              className="w-full mx-auto flex items-center justify-center gap-4 px-8 py-4 bg-white border-2 border-gray-300 rounded-xl hover:bg-gray-50 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-md hover:shadow-lg"
              style={{ width: '333px', height: '69px' }}
            >
              {isLoading ? (
                <>
                  <div className="w-6 h-6 border-2 border-gray-300 border-t-blue-600 rounded-full animate-spin"></div>
                  <span className="text-black font-medium text-lg">
                    處理中...
                  </span>
                </>
              ) : (
                <>
                  <svg className="w-6 h-6" viewBox="0 0 24 24">
                    <path
                      fill="#4285F4"
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    />
                    <path
                      fill="#34A853"
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    />
                    <path
                      fill="#FBBC05"
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    />
                    <path
                      fill="#EA4335"
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    />
                  </svg>
                  <span className="text-black font-medium text-lg">
                    使用 Google 帳號登入
                  </span>
                </>
              )}
            </button>
          </div>

          {/* 功能特色 */}
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <svg
                  className="w-6 h-6 text-blue-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 10V3L4 14h7v7l9-11h-7z"
                  />
                </svg>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">快速註冊</h3>
                <p className="text-sm text-gray-600 bg-white border border-white">
                  使用 Google 帳號即可快速註冊，無需填寫繁瑣表單
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <svg
                  className="w-6 h-6 text-green-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">自動設定</h3>
                <p className="text-sm text-gray-600 bg-white border border-white">
                  系統會自動從您的 Google Email 產生商家 ID
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <svg
                  className="w-6 h-6 text-purple-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4"
                  />
                </svg>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">完整功能</h3>
                <p className="text-sm text-gray-600 bg-white border border-white">
                  立即使用 POS 系統、預訂管理、訂單處理等功能
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                <svg
                  className="w-6 h-6 text-orange-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                  />
                </svg>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">安全可靠</h3>
                <p className="text-sm text-gray-600 bg-white border border-white">
                  使用 Google OAuth 認證，資料安全有保障
                </p>
              </div>
            </div>
          </div>

          {/* 說明區塊 */}
          <div className="mt-12 pt-8 border-t border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              註冊說明
            </h3>
            <ul className="space-y-3 text-sm text-gray-600">
              <li className="flex items-start">
                <span className="mr-3 text-blue-600 font-bold">•</span>
                <span>使用您的 Google 帳號即可快速註冊，無需額外填寫資料</span>
              </li>
              <li className="flex items-start">
                <span className="mr-3 text-blue-600 font-bold">•</span>
                <span>系統會自動從您的 Google Email 產生唯一的商家 ID</span>
              </li>
              <li className="flex items-start">
                <span className="mr-3 text-blue-600 font-bold">•</span>
                <span>註冊成功後，您可以使用商家 ID 登入 POS 系統</span>
              </li>
              <li className="flex items-start">
                <span className="mr-3 text-blue-600 font-bold">•</span>
                <span>網域可稍後再設定，用於預訂系統的網域識別</span>
              </li>
              <li className="flex items-start">
                <span className="mr-3 text-blue-600 font-bold">•</span>
                <span>如果您的 Google 帳號已註冊過，將直接登入系統</span>
              </li>
            </ul>
          </div>
        </div>

        {/* 底部資訊 */}
        <div className="mt-12 text-center text-sm text-gray-500">
          <p>需要協助？請聯繫我們的客服團隊</p>
        </div>
      </div>

      {/* 註冊商家 Modal */}
      {token && (
        <TenantRegistrationModal
          isOpen={showRegistrationModal}
          onClose={() => {
            setShowRegistrationModal(false);
            // 如果關閉 Modal，清除 action 參數
            const newUrl = new URL(window.location.href);
            newUrl.searchParams.delete('action');
            window.history.replaceState({}, '', newUrl.toString());
          }}
          onSuccess={handleRegistrationSuccess}
          token={token}
        />
      )}
    </div>
  );
}
